import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { checkPlanQuota, checkPlannedRoundsCapacity } from '../services/planService';
import { isAdsAvailable, preloadRewarded, showRewarded } from '../services/adService';
import {
  trackRewardedGateShown,
  trackRewardedGateDismissed,
  trackRewardedGateUpgradeClick,
  trackRewardedUnavailablePassthrough,
} from '../services/analytics';
import RewardedGateSheet from '../components/RewardedGateSheet';

// iOS rewarded ad-gate. Free users get 1 free Smart Schedule run per month and
// 1 free concurrent upcoming round; beyond that, each additional action prompts
// a rewarded ad ("watch to continue, or go Pro"). Off native iOS and for Pro
// users, gate() is a transparent no-op (returns true immediately). On web the
// free tier is monetised by AdSense display units instead (see AdSlot.jsx).
//
// Usage:
//   const { gate } = useRewardedGate();
//   if (!(await gate('smart_schedule'))) return; // user declined the gate

const RewardedGateContext = createContext(null);

// The 1st use of a gated feature is free; the 2nd+ prompts an ad.
const FREE_ALLOWANCE = 1;

export function RewardedGateProvider({ children }) {
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null); // { feature } | null
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  // Preload a rewarded ad for free iOS users so a gate shows instantly. This
  // also drives the one-time UMP consent + ATT prompts - contextual, and only
  // for users who can actually see ads (free, on iOS).
  useEffect(() => {
    if (isAdsAvailable() && !isPro) {
      preloadRewarded();
    }
  }, [isPro]);

  const finish = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setSheet(null);
    setBusy(false);
    if (resolve) resolve(value);
  }, []);

  const getCurrentCount = useCallback(async (feature) => {
    try {
      if (feature === 'smart_schedule') {
        const quota = await checkPlanQuota('smart_schedule', user);
        return quota?.current ?? 0;
      }
      if (feature === 'planned_round') {
        const cap = await checkPlannedRoundsCapacity(user);
        return cap?.current ?? 0;
      }
    } catch (err) {
      console.warn('[RewardedGate] count lookup failed (allowing):', err?.message || err);
    }
    return 0;
  }, [user]);

  // Returns a Promise<boolean>: true to proceed, false to abort.
  const gate = useCallback(async (feature) => {
    // No gate off native iOS or for Pro users.
    if (!isAdsAvailable() || isPro) return true;

    const current = await getCurrentCount(feature);
    if (current < FREE_ALLOWANCE) return true;

    trackRewardedGateShown(feature);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setSheet({ feature });
    });
  }, [isPro, getCurrentCount]);

  const handleWatch = useCallback(async () => {
    const feature = sheet?.feature;
    setBusy(true);
    const { shown, rewarded } = await showRewarded(feature);
    if (!shown) {
      // Fail-open: never hard-block a core action behind an unavailable ad.
      trackRewardedUnavailablePassthrough(feature);
      finish(true);
      return;
    }
    // Proceed only if the reward was actually earned (not closed early).
    finish(rewarded);
  }, [sheet, finish]);

  const handleUpgrade = useCallback(() => {
    const feature = sheet?.feature;
    trackRewardedGateUpgradeClick(feature);
    finish(false);
    navigate('/upgrade', { state: { reason: feature } });
  }, [sheet, finish, navigate]);

  const handleClose = useCallback(() => {
    trackRewardedGateDismissed(sheet?.feature);
    finish(false);
  }, [sheet, finish]);

  return (
    <RewardedGateContext.Provider value={{ gate }}>
      {children}
      <RewardedGateSheet
        isOpen={!!sheet}
        feature={sheet?.feature}
        busy={busy}
        onWatch={handleWatch}
        onUpgrade={handleUpgrade}
        onClose={handleClose}
      />
    </RewardedGateContext.Provider>
  );
}

// Fail-open no-op when used outside a provider (e.g. unauthenticated areas).
export const useRewardedGate = () => useContext(RewardedGateContext) || { gate: async () => true };
